import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, Settings, ChevronDown, Users, Calendar, BookOpen } from 'lucide-react';
import { Button } from './ui/button';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { authService } from '../services/authService';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [tutor, setTutor] = useState(null);
  const [tutorLoading, setTutorLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const { customer, loading: customerLoading, logout: customerLogout } = useCustomerAuth();

  const isHomePage = location.pathname === '/';

  const userType = customer ? 'customer' : tutor ? 'tutor' : null;
  const isLoggedIn = !!userType;

  useEffect(() => {
    const checkTutorAuth = async () => {
      const token = localStorage.getItem('tutorToken');
      if (!token) {
        setTutorLoading(false);
        return;
      }

      try {
        const response = await authService.getMe();
        if (response.success) {
          setTutor(response.data);
        } else {
          localStorage.removeItem('tutorToken');
          localStorage.removeItem('tutorData');
        }
      } catch (err) {
        localStorage.removeItem('tutorToken');
        localStorage.removeItem('tutorData');
      } finally {
        setTutorLoading(false);
      }
    };

    checkTutorAuth();
  }, []);

  const handleLogout = async () => {
    if (userType === 'customer') {
      await customerLogout();
      navigate('/');
    } else if (userType === 'tutor') {
      await authService.logout();
      setTutor(null);
      navigate('/');
    }
    setIsMenuOpen(false);
  };

  const getUserName = () => {
    if (userType === 'customer') {
      return customer?.name || customer?.customer_name || 'Customer';
    }
    if (userType === 'tutor') {
      return tutor?.name || tutor?.tutor_name || 'Tutor';
    }
    return '';
  };

  const isLoading = customerLoading || tutorLoading;

  const renderNavLinks = () => {
    if (!isLoggedIn) return null;

    if (userType === 'customer') {
      return (
        <div className="hidden md:flex items-center space-x-6">
          <Link
            to="/customer/dashboard"
            className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium"
          >
            Dashboard
          </Link>
          <Link
            to="/customer/students"
            className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium"
          >
            My Students
          </Link>
          <Link
            to="/customer/bookings"
            className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium"
          >
            My Bookings
          </Link>
        </div>
      );
    }

    if (userType === 'tutor') {
      return (
        <div className="hidden md:flex items-center space-x-6">
          <Link
            to="/tutor/dashboard"
            className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium"
          >
            Dashboard
          </Link>
          <Link
            to="/tutor/demo-bookings"
            className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium"
          >
            Demo Bookings
          </Link>
          <Link
            to="/tutor/settings"
            className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium"
          >
            Settings
          </Link>
        </div>
      );
    }
  };

  const renderMobileNavLinks = () => {
    if (!isLoggedIn) return null;

    if (userType === 'customer') {
      return (
        <>
          <Link
            to="/customer/dashboard"
            className="flex items-center gap-2 py-2 text-gray-700 hover:text-blue-600 transition-colors duration-200"
            onClick={() => setIsMenuOpen(false)}
          >
            <User className="h-4 w-4" />
            Dashboard
          </Link>
          <Link
            to="/customer/students"
            className="flex items-center gap-2 py-2 text-gray-700 hover:text-blue-600 transition-colors duration-200"
            onClick={() => setIsMenuOpen(false)}
          >
            <Users className="h-4 w-4" />
            My Students
          </Link>
          <Link
            to="/customer/bookings"
            className="flex items-center gap-2 py-2 text-gray-700 hover:text-blue-600 transition-colors duration-200"
            onClick={() => setIsMenuOpen(false)}
          >
            <Calendar className="h-4 w-4" />
            My Bookings
          </Link>
        </>
      );
    }

    if (userType === 'tutor') {
      return (
        <>
          <Link
            to="/tutor/dashboard"
            className="flex items-center gap-2 py-2 text-gray-700 hover:text-blue-600 transition-colors duration-200"
            onClick={() => setIsMenuOpen(false)}
          >
            <User className="h-4 w-4" />
            Dashboard
          </Link>
          <Link
            to="/tutor/demo-bookings"
            className="flex items-center gap-2 py-2 text-gray-700 hover:text-blue-600 transition-colors duration-200"
            onClick={() => setIsMenuOpen(false)}
          >
            <BookOpen className="h-4 w-4" />
            Demo Bookings
          </Link>
          <Link
            to="/tutor/settings"
            className="flex items-center gap-2 py-2 text-gray-700 hover:text-blue-600 transition-colors duration-200"
            onClick={() => setIsMenuOpen(false)}
          >
            <Settings className="h-4 w-4" />
            Settings
          </Link>
        </>
      );
    }
  };

  const renderAuthButtons = () => {
    if (isLoading) {
      return (
        <div className="hidden md:flex items-center">
          <div className="h-9 w-20 bg-gray-200 animate-pulse rounded-md" />
        </div>
      );
    }

    if (isLoggedIn) {
      const settingsPath = userType === 'customer' ? '/customer/dashboard' : '/tutor/settings';

      return (
        <div className="hidden md:flex items-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 hover:bg-gray-100">
                <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-600 to-teal-500 flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {getUserName().charAt(0).toUpperCase()}
                  </span>
                </div>
                <span className="text-gray-700 font-medium">{getUserName()}</span>
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                  {userType === 'customer' ? 'Customer' : 'Tutor'}
                </span>
                <ChevronDown className="h-4 w-4 text-gray-500" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span>{getUserName()}</span>
                  <span className="text-xs text-gray-500 font-normal">
                    {userType === 'customer' ? 'Customer Account' : 'Tutor Account'}
                  </span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to={settingsPath} className="flex items-center gap-2 cursor-pointer">
                  <Settings className="h-4 w-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="flex items-center gap-2 cursor-pointer text-red-600 focus:text-red-600"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    }

    return (
      <div className="hidden md:flex items-center space-x-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="text-gray-700 hover:text-blue-600 flex items-center gap-1">
              Login
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to="/customer/login" className="cursor-pointer">
                Guardian Login
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/tutor/login" className="cursor-pointer">
                Tutor Login
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-1">
              Register
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to="/customer/login" className="cursor-pointer">
                Guardian Register
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/tutor/register" className="cursor-pointer">
                Tutor Register
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  const renderMobileAuthButtons = () => {
    if (isLoading) {
      return (
        <div className="h-9 w-full bg-gray-200 animate-pulse rounded-md" />
      );
    }

    if (isLoggedIn) {
      const settingsPath = userType === 'customer' ? '/customer/dashboard' : '/tutor/settings';

      return (
        <>
          <div className="flex items-center gap-2 py-2">
            <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-600 to-teal-500 flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {getUserName().charAt(0).toUpperCase()}
              </span>
            </div>
            <span className="text-gray-700 font-medium">{getUserName()}</span>
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
              {userType === 'customer' ? 'Customer' : 'Tutor'}
            </span>
          </div>
          <Link to={settingsPath} className="block" onClick={() => setIsMenuOpen(false)}>
            <Button variant="ghost" className="w-full justify-start text-gray-700 hover:text-blue-600">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
          </Link>
          <Button
            variant="ghost"
            className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </>
      );
    }

    return (
      <>
        <div className="space-y-2">
          <p className="text-xs text-gray-500 font-medium px-1">Login as:</p>
          <Link to="/customer/login" className="block" onClick={() => setIsMenuOpen(false)}>
            <Button variant="ghost" className="w-full justify-start text-gray-700 hover:text-blue-600">
              Guardian Login
            </Button>
          </Link>
          <Link to="/tutor/login" className="block" onClick={() => setIsMenuOpen(false)}>
            <Button variant="ghost" className="w-full justify-start text-gray-700 hover:text-blue-600">
              Tutor Login
            </Button>
          </Link>
        </div>
        <div className="space-y-2 pt-2 border-t border-gray-200">
          <p className="text-xs text-gray-500 font-medium px-1">Register as:</p>
          <Link to="/customer/login" className="block" onClick={() => setIsMenuOpen(false)}>
            <Button className="w-full bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white">
              Guardian Register
            </Button>
          </Link>
          <Link to="/tutor/register" className="block" onClick={() => setIsMenuOpen(false)}>
            <Button className="w-full bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white">
              Become a Tutor
            </Button>
          </Link>
        </div>
      </>
    );
  };

  return (
    <nav className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2 group">
            <img
              src={`${process.env.PUBLIC_URL}/priedu_logo.png`}
              alt="PriEdu Logo"
              className="h-14 w-auto rounded-lg group-hover:shadow-lg transition-all duration-300"
            />
            {!isHomePage && (
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">
              </span>
            )}
          </Link>

          {isHomePage && !isLoggedIn && (
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-700 hover:text-blue-600 transition-colors duration-200">
                Features
              </a>
              <a href="#tutors" className="text-gray-700 hover:text-blue-600 transition-colors duration-200">
                Find Tutors
              </a>
              <a href="#how-it-works" className="text-gray-700 hover:text-blue-600 transition-colors duration-200">
                How It Works
              </a>
              <a href="#testimonials" className="text-gray-700 hover:text-blue-600 transition-colors duration-200">
                Testimonials
              </a>
            </div>
          )}

          {renderNavLinks()}

          {renderAuthButtons()}

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-4 space-y-3">
            {isHomePage && !isLoggedIn && (
              <>
                <a href="#features" className="block py-2 text-gray-700 hover:text-blue-600 transition-colors duration-200">
                  Features
                </a>
                <a href="#tutors" className="block py-2 text-gray-700 hover:text-blue-600 transition-colors duration-200">
                  Find Tutors
                </a>
                <a href="#how-it-works" className="block py-2 text-gray-700 hover:text-blue-600 transition-colors duration-200">
                  How It Works
                </a>
                <a href="#testimonials" className="block py-2 text-gray-700 hover:text-blue-600 transition-colors duration-200">
                  Testimonials
                </a>
              </>
            )}

            {renderMobileNavLinks()}

            {renderMobileAuthButtons()}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
